// CRM-owned parts of the Lead Detail modal: assignment, CRM status, added
// social profile links and logged interactions.
//
// Everything here is loaded from /api/lead-crm on top of the Sheet-provided
// lead. A failed overlay request never hides the lead itself - the sections
// only report their own state and offer one retry.

import { useCallback, useEffect, useId, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { ExternalLink, Plus, X } from 'lucide-react'
import { DateTimePicker, toLocalDateTimeValue } from '../../components/ui/DateTimePicker'
import { Select } from '../../components/ui/Select'
import {
  CRM_STATUSES,
  INTERACTION_TYPES,
  INTERACTION_TYPE_LABELS,
  SOCIAL_PLATFORMS,
  SOCIAL_PLATFORM_LABELS,
  addLeadSocialLink,
  createLeadInteraction,
  fetchLeadCrm,
  removeLeadSocialLink,
  saveLeadCrmProfile,
  updateLeadSocialLink,
  type CrmStatus,
  type LeadCrmSnapshot,
  type LeadInteraction,
  type LeadSocialLink,
  type SocialPlatform,
  type StaffMember,
} from './leadCrmClient'
import { SHEET_SOCIAL_FIELDS, type SheetSocialField } from './leadSourceClient'
import { SocialIcon } from './SocialIcon'

export type LeadCrmState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; data: LeadCrmSnapshot }

export type LeadCrm = {
  leadKey: string
  state: LeadCrmState
  canRetry: boolean
  retry: () => void
  update: (next: (data: LeadCrmSnapshot) => LeadCrmSnapshot) => void
}

export function useLeadCrm(leadKey: string): LeadCrm {
  const [state, setState] = useState<LeadCrmState>({ status: 'loading' })
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    // Without a stable identity there is no key to store CRM state against.
    if (!leadKey) {
      setState({
        status: 'error',
        message: 'This lead has no stable identity yet, so CRM details cannot be stored against it.',
      })
      return
    }

    const controller = new AbortController()
    let live = true

    setState({ status: 'loading' })

    fetchLeadCrm(leadKey, controller.signal)
      .then((data) => {
        if (live) setState({ status: 'ready', data })
      })
      .catch((cause: unknown) => {
        if (!live || controller.signal.aborted) return
        setState({
          status: 'error',
          message: cause instanceof Error ? cause.message : 'The CRM workspace could not be loaded.',
        })
      })

    return () => {
      live = false
      controller.abort()
    }
  }, [leadKey, attempt])

  const update = useCallback((next: (data: LeadCrmSnapshot) => LeadCrmSnapshot) => {
    setState((current) => (current.status === 'ready' ? { status: 'ready', data: next(current.data) } : current))
  }, [])

  const retry = useCallback(() => setAttempt((value) => value + 1), [])

  return { leadKey, state, canRetry: Boolean(leadKey), retry, update }
}

export function messageOf(cause: unknown, fallback: string): string {
  return cause instanceof Error && cause.message ? cause.message : fallback
}

// Interactions are shown by day only; the stored timestamp keeps its time.
function formatOccurredOn(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(date)
}

function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

function OverlayState({ crm, label }: { crm: LeadCrm; label: string }) {
  if (crm.state.status === 'loading') {
    return <p className="lead-crm-state">Loading {label}...</p>
  }

  return <p className="lead-crm-state">{label} are unavailable right now.</p>
}

/* ---------------------------------------------------------------- CRM meta */

export function LeadCrmMeta({ crm, children }: { crm: LeadCrm; children?: ReactNode }) {
  const snapshot = crm.state.status === 'ready' ? crm.state.data : null
  const ready = snapshot !== null
  const profile = snapshot?.profile ?? null
  const staff = snapshot ? snapshot.staff.filter((member) => member.active) : []
  const [saving, setSaving] = useState<'' | 'status' | 'assignee'>('')
  const [error, setError] = useState('')
  const fieldId = useId()

  async function save(changes: Parameters<typeof saveLeadCrmProfile>[1], field: 'status' | 'assignee') {
    setError('')
    setSaving(field)

    try {
      const saved = await saveLeadCrmProfile(crm.leadKey, changes)
      crm.update((current) => ({ ...current, profile: saved }))
    } catch (cause) {
      setError(messageOf(cause, 'The CRM change could not be saved.'))
    } finally {
      setSaving('')
    }
  }

  return (
    <section aria-label="CRM" className="lead-crm-bar-section">
      <div className="lead-crm-bar">
        <div className="lead-crm-field">
          <label htmlFor={`${fieldId}-status`}>CRM Status</label>
          <Select
            disabled={!ready || saving === 'status'}
            id={`${fieldId}-status`}
            onChange={(value) => save({ crmStatus: (value || null) as CrmStatus | null }, 'status')}
            options={[{ value: '', label: 'Not set' }, ...CRM_STATUSES.map((status) => ({ value: status, label: status }))]}
            value={profile?.crmStatus ?? ''}
          />
        </div>

        <div className="lead-crm-field">
          <label htmlFor={`${fieldId}-assignee`}>Assigned To</label>
          <Select
            disabled={!ready || saving === 'assignee'}
            id={`${fieldId}-assignee`}
            onChange={(value) => save({ assignedStaffId: value || null }, 'assignee')}
            options={[
              { value: '', label: 'Unassigned' },
              ...staff.map((member) => ({ value: member.id, label: member.displayName })),
            ]}
            value={profile?.assignedStaffId ?? ''}
          />
        </div>

        {children ? <div className="lead-crm-bar-actions">{children}</div> : null}
      </div>

      {crm.state.status === 'error' ? (
        <div className="lead-crm-state" role="status">
          <span>{crm.state.message}</span>
          {crm.canRetry ? (
            <button className="lead-crm-retry" onClick={crm.retry} type="button">
              Retry
            </button>
          ) : null}
        </div>
      ) : null}

      {saving ? <p className="lead-crm-state" role="status">Saving...</p> : null}
      {error ? <p className="lead-crm-error" role="alert">{error}</p> : null}
    </section>
  )
}

/* ---------------------------------------------------------- Social profiles */

export type SourceSocialLink = {
  label: string
  url: string
  /** Set for the Sheet's Facebook / Instagram / LinkedIn columns, which can be edited. */
  sheetField?: SheetSocialField
}

export type SocialRow = {
  key: string
  platform: string
  label: string
  url: string
  link: LeadSocialLink | null
  sheetField?: SheetSocialField
}

/** Writes one Sheet-owned social column; present only for a lead with an Organizations row. */
export type SheetSocialSave = (field: SheetSocialField, url: string) => Promise<void>

const isSheetSocial = (platform: SocialPlatform): platform is SheetSocialField =>
  (SHEET_SOCIAL_FIELDS as readonly string[]).includes(platform)

function normalizeUrlKey(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/+$/, '')
}

/**
 * Sheet links and CRM links in one list, with an identical URL shown once.
 *
 * `sheetOwned` (the lead has an Organizations row): a filled Sheet
 * Facebook/Instagram/LinkedIn is the only copy shown, and a legacy Supabase
 * link for that platform is hidden, never deleted. With the Sheet cell blank,
 * the legacy link stays visible as a fallback. Otherwise the CRM entry wins.
 */
export function combineSocialRows(
  crmLinks: LeadSocialLink[],
  sourceLinks: SourceSocialLink[],
  sheetOwned = false,
): SocialRow[] {
  const sheetLinks = sheetOwned ? sourceLinks.filter((source) => source.sheetField) : []
  const sheetPlatforms = new Set<string>(sheetLinks.map((source) => source.sheetField as string))
  const sourceRow = (source: SourceSocialLink): SocialRow => ({
    key: `source:${normalizeUrlKey(source.url)}`,
    platform: source.label,
    label: '',
    url: source.url,
    link: null,
    sheetField: source.sheetField,
  })

  const candidates: SocialRow[] = [
    ...sheetLinks.map(sourceRow),
    ...crmLinks
      .filter((link) => !sheetPlatforms.has(link.platform))
      .map((link) => ({
        key: link.id,
        platform: SOCIAL_PLATFORM_LABELS[link.platform] ?? 'Other',
        label: link.label ?? '',
        url: link.url,
        link,
      })),
    ...sourceLinks.filter((source) => !sheetLinks.includes(source)).map(sourceRow),
  ]

  const seen = new Set<string>()
  return candidates.filter((row) => {
    const urlKey = normalizeUrlKey(row.url)
    if (!urlKey || seen.has(urlKey)) return false
    seen.add(urlKey)
    return true
  })
}

const PLATFORM_HOSTS: [RegExp, SocialPlatform][] = [
  [/(^|\.)(facebook\.com|fb\.com)$/, 'facebook'],
  [/(^|\.)instagram\.com$/, 'instagram'],
  [/(^|\.)linkedin\.com$/, 'linkedin'],
  [/(^|\.)tiktok\.com$/, 'tiktok'],
  [/(^|\.)(youtube\.com|youtu\.be)$/, 'youtube'],
  [/(^|\.)(x\.com|twitter\.com)$/, 'x'],
]

/**
 * Sheet links only carry a label, so they are matched back to a platform by
 * name; a generic "Profile" link is matched by its host instead.
 */
function platformOf(row: SocialRow): SocialPlatform {
  if (row.link) return row.link.platform
  if (row.sheetField) return row.sheetField
  const label = row.platform.toLowerCase()
  const named = SOCIAL_PLATFORMS.find((platform) => SOCIAL_PLATFORM_LABELS[platform].toLowerCase() === label)
  if (named) return named
  let host = ''
  try {
    host = new URL(row.url).hostname.toLowerCase()
  } catch {
    return 'other'
  }
  return PLATFORM_HOSTS.find(([pattern]) => pattern.test(host))?.[1] ?? 'other'
}

const EMPTY_SOCIAL_FORM = { platform: 'facebook' as SocialPlatform, label: '', url: '' }

export function LeadSocialProfiles({
  crm,
  sourceLinks,
  saveSheetSocial,
}: {
  crm: LeadCrm
  sourceLinks: SourceSocialLink[]
  saveSheetSocial?: SheetSocialSave
}) {
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingSheet, setEditingSheet] = useState<SheetSocialField | null>(null)
  const [adding, setAdding] = useState(false)
  const [form, setForm] = useState(EMPTY_SOCIAL_FORM)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const formId = useId()

  const crmLinks = crm.state.status === 'ready' ? crm.state.data.socialLinks : []
  const rows = combineSocialRows(crmLinks, sourceLinks, Boolean(saveSheetSocial))
  const formOpen = adding || editingId !== null || editingSheet !== null

  // Storage follows the platform, never the URL: Facebook, Instagram and
  // LinkedIn go to their Sheet column when the lead has an Organizations row;
  // TikTok, YouTube, X and Other stay Supabase CRM links. Without an
  // Organizations row (prospecting-only) every platform stays in Supabase.
  const sheetTarget = editingSheet ?? (adding && saveSheetSocial && isSheetSocial(form.platform) ? form.platform : null)

  // One link per platform: a platform already present in the sheet or the CRM
  // is not offered again, except to the CRM link that is being edited. A Sheet
  // profile being edited keeps its own platform only.
  const usedPlatforms = new Set(rows.filter((row) => !row.link || row.link.id !== editingId).map(platformOf))
  const availablePlatforms = editingSheet
    ? [editingSheet]
    : SOCIAL_PLATFORMS.filter((platform) => !usedPlatforms.has(platform))
  const allPlatformsAdded = availablePlatforms.length === 0 && !formOpen

  function openAdd() {
    if (!availablePlatforms.length) return
    setEditingId(null)
    setEditingSheet(null)
    setForm({ ...EMPTY_SOCIAL_FORM, platform: availablePlatforms[0] })
    setError('')
    setAdding(true)
  }

  function openEdit(link: LeadSocialLink) {
    // A legacy Supabase Facebook/Instagram/LinkedIn fallback is edited into
    // the Sheet, which then becomes its only shown copy.
    if (saveSheetSocial && isSheetSocial(link.platform)) {
      openSheetEdit(link.platform, link.url)
      return
    }
    setAdding(false)
    setEditingSheet(null)
    setForm({ platform: link.platform, label: link.label ?? '', url: link.url })
    setError('')
    setEditingId(link.id)
  }

  function openSheetEdit(field: SheetSocialField, url: string) {
    setAdding(false)
    setEditingId(null)
    setForm({ platform: field, label: '', url })
    setError('')
    setEditingSheet(field)
  }

  function closeForm() {
    setAdding(false)
    setEditingId(null)
    setEditingSheet(null)
    setError('')
  }

  async function submit(event: FormEvent) {
    event.preventDefault()
    setError('')

    if (!form.url.trim()) {
      setError('A profile URL is required.')
      return
    }

    setSaving(true)

    try {
      if (sheetTarget && saveSheetSocial) {
        await saveSheetSocial(sheetTarget, form.url.trim())
      } else if (editingId) {
        const saved = await updateLeadSocialLink(crm.leadKey, editingId, form)
        crm.update((current) => ({
          ...current,
          socialLinks: current.socialLinks.map((link) => (link.id === saved.id ? saved : link)),
        }))
      } else {
        const saved = await addLeadSocialLink(crm.leadKey, form)
        crm.update((current) => ({ ...current, socialLinks: [...current.socialLinks, saved] }))
      }

      closeForm()
    } catch (cause) {
      setError(messageOf(cause, 'The social profile link could not be saved.'))
    } finally {
      setSaving(false)
    }
  }

  async function remove(link: LeadSocialLink, label: string) {
    setError('')
    setSaving(true)

    try {
      await removeLeadSocialLink(crm.leadKey, link.id)
      crm.update((current) => ({
        ...current,
        socialLinks: current.socialLinks.filter((entry) => entry.id !== link.id),
      }))
    } catch (cause) {
      setError(messageOf(cause, `The ${label} link could not be removed.`))
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className="lead-modal-section" aria-labelledby="lead-social-title">
      <div className="lead-crm-heading">
        <h3 id="lead-social-title">Social Profiles</h3>
        <span className="lead-crm-heading-actions">
          {crm.state.status === 'ready' && allPlatformsAdded ? (
            <span className="lead-crm-hint" id={`${formId}-all`}>All platforms added</span>
          ) : null}
          <button
            aria-describedby={crm.state.status === 'ready' && allPlatformsAdded ? `${formId}-all` : undefined}
            className="lead-crm-action"
            disabled={crm.state.status !== 'ready' || formOpen || allPlatformsAdded}
            onClick={openAdd}
            type="button"
          >
            <Plus aria-hidden="true" size={13} />
            Add
          </button>
        </span>
      </div>

      {rows.length ? (
        <ul className="lead-crm-socials">
          {rows.map((row) => {
            const link = row.link

            return (
              <li className="lead-crm-social" key={row.key}>
                <span className="lead-crm-social-platform">
                  <SocialIcon platform={platformOf(row)} />
                  {row.platform}
                </span>
                <a aria-label={`Open ${row.platform}`} href={row.url} rel="noreferrer" target="_blank">
                  <span>{row.label || row.url.replace(/^https?:\/\/(www\.)?/i, '')}</span>
                  <ExternalLink aria-hidden="true" size={12} />
                </a>
                {row.sheetField && saveSheetSocial ? (
                  <span className="lead-crm-social-actions">
                    <button
                      aria-label={`Edit ${row.platform} link`}
                      onClick={() => openSheetEdit(row.sheetField as SheetSocialField, row.url)}
                      type="button"
                    >
                      Edit
                    </button>
                  </span>
                ) : null}
                {link ? (
                  <span className="lead-crm-social-actions">
                    <button aria-label={`Edit ${row.platform} link`} onClick={() => openEdit(link)} type="button">
                      Edit
                    </button>
                    <button
                      aria-label={`Remove ${row.platform} link`}
                      onClick={() => remove(link, row.platform)}
                      type="button"
                    >
                      Remove
                    </button>
                  </span>
                ) : null}
              </li>
            )
          })}
        </ul>
      ) : crm.state.status === 'ready' ? (
        <p className="lead-crm-state">No social profiles recorded.</p>
      ) : (
        <OverlayState crm={crm} label="Social profiles" />
      )}

      {/* Kept mounted so it can expand and collapse; inert while closed. */}
      <div aria-hidden={!formOpen} className="lead-crm-collapse" data-open={formOpen || undefined} inert={!formOpen}>
        <div className="lead-crm-collapse-inner">
          <form className="lead-crm-form" onSubmit={submit}>
            <div className="lead-crm-form-row">
              <div className="lead-crm-field">
                <label htmlFor={`${formId}-platform`}>Platform</label>
                <Select
                  id={`${formId}-platform`}
                  onChange={(value) => setForm((current) => ({ ...current, platform: value as SocialPlatform }))}
                  options={availablePlatforms.map((platform) => ({
                    value: platform,
                    label: SOCIAL_PLATFORM_LABELS[platform],
                    icon: <SocialIcon platform={platform} />,
                  }))}
                  value={form.platform}
                />
              </div>
  
              {/* The Sheet has no label column, so Sheet-owned profiles take a URL only. */}
              {sheetTarget ? null : (
                <label className="lead-crm-field" htmlFor={`${formId}-label`}>
                  <span>Label (optional)</span>
                  <input
                    id={`${formId}-label`}
                    maxLength={120}
                    onChange={(event) => setForm((current) => ({ ...current, label: event.target.value }))}
                    placeholder="e.g. Main page"
                    value={form.label}
                  />
                </label>
              )}
            </div>
  
            <label className="lead-crm-field" htmlFor={`${formId}-url`}>
              <span>URL</span>
              <input
                id={`${formId}-url`}
                maxLength={500}
                onChange={(event) => setForm((current) => ({ ...current, url: event.target.value }))}
                placeholder="https://instagram.com/..."
                value={form.url}
              />
            </label>
  
            {formOpen && error ? <p className="lead-crm-error" role="alert">{error}</p> : null}
  
            <div className="lead-crm-form-actions">
              <button className="button button-secondary lead-crm-small" disabled={saving} type="submit">
                {editingId || editingSheet ? 'Save link' : 'Add link'}
              </button>
              <button
                className="lead-crm-action"
                disabled={saving}
                onClick={closeForm}
                type="button"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
      {!formOpen && error ? <p className="lead-crm-error" role="alert">{error}</p> : null}
    </section>
  )
}

/* -------------------------------------------------------------- Timeline */

export function LeadInteractions({ crm }: { crm: LeadCrm }) {
  const interactions = crm.state.status === 'ready' ? crm.state.data.interactions : []
  const staff = crm.state.status === 'ready' ? crm.state.data.staff : []

  return (
    <section className="lead-modal-section lead-crm-interactions" aria-labelledby="lead-interactions-title">
      <div className="lead-crm-heading">
        <h3 id="lead-interactions-title">Recent Interactions</h3>
        {interactions.length ? <span className="lead-crm-count">{interactions.length}</span> : null}
      </div>

      {crm.state.status === 'ready' && !interactions.length ? (
        <p className="lead-crm-state">No interactions logged yet.</p>
      ) : null}

      {crm.state.status === 'loading' || (crm.state.status === 'error' && !interactions.length) ? (
        <OverlayState crm={crm} label="Interactions" />
      ) : null}

      {interactions.length ? (
        <ol className="lead-crm-timeline">
          {interactions.map((interaction) => {
            const handledBy = staff.find((member) => member.id === interaction.handledByStaffId)
            const details = [
              ['Outcome', interaction.outcome],
              ['Next action', interaction.nextAction],
              ['Handled by', handledBy?.displayName],
            ].filter((entry): entry is [string, string] => Boolean(entry[1]))

            return (
              <li className="lead-crm-entry" key={interaction.id}>
                <div className="lead-crm-entry-head">
                  <time dateTime={interaction.occurredAt}>{formatOccurredOn(interaction.occurredAt)}</time>
                  <span className="lead-crm-entry-type">
                    {INTERACTION_TYPE_LABELS[interaction.interactionType] ?? interaction.interactionType}
                  </span>
                  {interaction.channel ? <span className="lead-crm-entry-channel">{interaction.channel}</span> : null}
                </div>
                <p className="lead-crm-entry-summary">{interaction.summary}</p>
                {details.length ? (
                  <dl className="lead-crm-entry-details">
                    {details.map(([label, value]) => (
                      <div key={label}>
                        <dt>{label}</dt>
                        <dd>{value}</dd>
                      </div>
                    ))}
                  </dl>
                ) : null}
              </li>
            )
          })}
        </ol>
      ) : null}
    </section>
  )
}

/* ----------------------------------------------------- Log interaction modal */

export function LogInteractionModal({
  leadKey,
  staff,
  onClose,
  onSaved,
}: {
  leadKey: string
  staff: StaffMember[]
  onClose: () => void
  onSaved: (interaction: LeadInteraction) => void
}) {
  const active = useMemo(() => staff.filter((member) => member.active), [staff])
  const [interactionType, setInteractionType] = useState('facebook-dm')
  const [channel, setChannel] = useState('')
  // The CRM runs for one owner; the selector still shows who is recorded.
  const [handledBy, setHandledBy] = useState(() => (active.length === 1 ? active[0].id : ''))
  const [occurredAt, setOccurredAt] = useState(() => toLocalDateTimeValue())
  const [summary, setSummary] = useState('')
  const [outcome, setOutcome] = useState('')
  const [nextAction, setNextAction] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [closing, setClosing] = useState(false)
  const closeTimerRef = useRef<number | null>(null)
  const fieldId = useId()

  const closeButton = useCallback((node: HTMLButtonElement | null) => node?.focus(), [])

  // Plays the exit animation before the parent unmounts the dialog.
  const requestClose = useCallback(() => {
    if (closeTimerRef.current !== null) return
    setClosing(true)
    closeTimerRef.current = window.setTimeout(onClose, prefersReducedMotion() ? 0 : 130)
  }, [onClose])

  useEffect(() => () => {
    if (closeTimerRef.current !== null) window.clearTimeout(closeTimerRef.current)
  }, [])

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') requestClose()
    }
    window.addEventListener('keydown', closeOnEscape)
    return () => window.removeEventListener('keydown', closeOnEscape)
  }, [requestClose])

  async function submit(event: FormEvent) {
    event.preventDefault()
    setError('')

    if (!summary.trim()) {
      setError('A summary is required.')
      return
    }

    const occurred = new Date(occurredAt)
    if (Number.isNaN(occurred.getTime())) {
      setError('A valid date and time is required.')
      return
    }

    setSaving(true)

    try {
      const saved = await createLeadInteraction(leadKey, {
        interactionType: interactionType as LeadInteraction['interactionType'],
        channel,
        occurredAt: occurred.toISOString(),
        handledByStaffId: handledBy || null,
        summary,
        outcome,
        nextAction,
      })

      onSaved(saved)
      requestClose()
    } catch (cause) {
      // The draft stays on screen so a failed save loses nothing.
      setError(messageOf(cause, 'The interaction could not be saved.'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className={`lead-modal-layer lead-crm-layer${closing ? ' is-closing' : ''}`}>
      <button aria-label="Close log interaction" className="lead-modal-backdrop" onClick={requestClose} type="button" />
      <section
        aria-labelledby="log-interaction-title"
        aria-modal="true"
        className="lead-modal lead-crm-dialog"
        role="dialog"
      >
        <header className="lead-crm-dialog-header">
          <h2 id="log-interaction-title">Log interaction</h2>
          <button
            aria-label="Close log interaction"
            className="icon-button lead-modal-close"
            onClick={requestClose}
            ref={closeButton}
            type="button"
          >
            <X size={20} />
          </button>
        </header>

        <form className="lead-crm-dialog-body" onSubmit={submit}>
          <div className="lead-crm-form-row">
            <div className="lead-crm-field">
              <label htmlFor={`${fieldId}-type`}>Interaction type</label>
              <Select
                id={`${fieldId}-type`}
                onChange={setInteractionType}
                options={INTERACTION_TYPES.map((type) => ({ value: type, label: INTERACTION_TYPE_LABELS[type] }))}
                value={interactionType}
              />
            </div>

            <label className="lead-crm-field" htmlFor={`${fieldId}-channel`}>
              <span>Channel (optional)</span>
              <input
                id={`${fieldId}-channel`}
                maxLength={120}
                onChange={(event) => setChannel(event.target.value)}
                placeholder="e.g. Facebook"
                value={channel}
              />
            </label>
          </div>

          <div className="lead-crm-form-row">
            <div className="lead-crm-field">
              <label htmlFor={`${fieldId}-when`}>Date</label>
              <DateTimePicker dateOnly id={`${fieldId}-when`} onChange={setOccurredAt} value={occurredAt} />
            </div>

            <div className="lead-crm-field">
              <label htmlFor={`${fieldId}-staff`}>Handled by</label>
              <Select
                id={`${fieldId}-staff`}
                onChange={setHandledBy}
                options={[
                  { value: '', label: 'Not recorded' },
                  ...active.map((member) => ({ value: member.id, label: member.displayName })),
                ]}
                value={handledBy}
              />
            </div>
          </div>

          <label className="lead-crm-field" htmlFor={`${fieldId}-summary`}>
            <span>Summary</span>
            <textarea
              id={`${fieldId}-summary`}
              maxLength={4000}
              onChange={(event) => setSummary(event.target.value)}
              rows={4}
              value={summary}
            />
          </label>

          <div className="lead-crm-form-row">
            <label className="lead-crm-field" htmlFor={`${fieldId}-outcome`}>
              <span>Outcome (optional)</span>
              <input
                id={`${fieldId}-outcome`}
                maxLength={2000}
                onChange={(event) => setOutcome(event.target.value)}
                value={outcome}
              />
            </label>

            <label className="lead-crm-field" htmlFor={`${fieldId}-next`}>
              <span>Next action (optional)</span>
              <input
                id={`${fieldId}-next`}
                maxLength={2000}
                onChange={(event) => setNextAction(event.target.value)}
                value={nextAction}
              />
            </label>
          </div>

          {error ? <p className="lead-crm-error" role="alert">{error}</p> : null}

          <div className="lead-crm-form-actions">
            <button className="button button-primary lead-crm-small" disabled={saving} type="submit">
              {saving ? 'Saving...' : 'Save interaction'}
            </button>
            <button className="lead-crm-action" disabled={saving} onClick={requestClose} type="button">
              Cancel
            </button>
          </div>
        </form>
      </section>
    </div>
  )
}
